import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Row,
  Col,
  DatePicker,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PictureOutlined,
  FileTextOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import masterService from "../../../services/masterService";
import ImageUpload from "../../../components/banners/ImageUpload";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const TALENT_FIELD_MAX_LENGTHS = {
  name: 255,
  alternateName: 255,
  slug: 255,
  nationality: 255,
  imdb_id: 255,
  description: 5000,
};

const AWARDS_MAX_LENGTH = 5000;

const MULTILINGUAL_FIELD_MAX_LENGTHS = {
  name: 255,
  alternateName: 255,
  nationality: 255,
  description: 5000,
};

const BILINGUAL_TEXT_SANITIZE_REGEX = /[^A-Za-z\u0600-\u06FF\s]/g;

function sanitizeBilingualTextInput(value = "") {
  return String(value).replace(BILINGUAL_TEXT_SANITIZE_REGEX, "");
}

const getCommaSeparatedLength = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return 0;
  }

  return values.reduce((total, item, index) => {
    const value = String(item || "");
    return total + value.length + (index > 0 ? 1 : 0);
  }, 0);
};


const EditCast = () => {
  const navigate = useNavigate();
  const { talent_id } = useParams();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const notify = useNotify();

  const fetchTalent = useCallback(async () => {
    try {
      const response = await masterService.getTalent(talent_id);
      console.log("response===>", response);
      if (response.data && response.data.status) {
        const talent = response.data.data;
        form.setFieldsValue({
          ...talent,
          birthDate: talent.birthDate ? dayjs(talent.birthDate) : null,
          deathDate: talent.deathDate ? dayjs(talent.deathDate) : null,
        });
      }
    } catch (error) {
      console.error("Error fetching talent:", error);
      notify.error({ message: "Error fetching talent" });
    }
  }, [talent_id, form, notify]);

  const handleSubmit = async (values) => {
    console.log(values);

    const fieldLabels = {
      name: "Name",
      alternateName: "Alternate Name",
      slug: "Slug",
      nationality: "Nationality",
      imdb_id: "IMDB ID",
      description: "Description",
      award: "Awards",
    };

    const exceededFields = Object.entries(TALENT_FIELD_MAX_LENGTHS).filter(
      ([field, maxLength]) => {
        const value = values[field];
        return typeof value === "string" && value.length > maxLength;
      }
    );

    if (exceededFields.length) {
      const formErrors = exceededFields.map(([field, maxLength]) => ({
        name: field,
        errors: [`${fieldLabels[field]} cannot exceed ${maxLength} characters`],
      }));

      form.setFields(formErrors);
      notify.error({
        message: `${fieldLabels[exceededFields[0][0]]} exceeds allowed limit`,
      });
      return;
    }

    const awardLength = getCommaSeparatedLength(values.award);
    if (awardLength > AWARDS_MAX_LENGTH) {
      form.setFields([
        {
          name: "award",
          errors: [`Awards cannot exceed ${AWARDS_MAX_LENGTH} characters`],
        },
      ]);
      notify.error({ message: "Awards exceeds allowed limit" });
      return;
    }

    const multilingualData = Array.isArray(values.multilingual_data)
      ? values.multilingual_data
      : [];

    const exceededMultilingualFields = [];
    multilingualData.forEach((entry, index) => {
      Object.entries(MULTILINGUAL_FIELD_MAX_LENGTHS).forEach(
        ([field, maxLength]) => {
          const value = entry?.[field];
          if (typeof value === "string" && value.length > maxLength) {
            exceededMultilingualFields.push({ index, field, maxLength });
          }
        }
      );
    });

    if (exceededMultilingualFields.length) {
      const formErrors = exceededMultilingualFields.map(
        ({ index, field, maxLength }) => ({
          name: ["multilingual_data", index, field],
          errors: [
            `${fieldLabels[field]} cannot exceed ${maxLength} characters`,
          ],
        })
      );

      form.setFields(formErrors);
      const firstError = exceededMultilingualFields[0];
      notify.error({
        message: `Language Entry #${firstError.index + 1} has values beyond limit`,
      });
      return;
    }

    const exceededMultilingualAwards = multilingualData
      .map((entry, index) => ({
        index,
        length: getCommaSeparatedLength(entry?.award),
      }))
      .filter(({ length }) => length > AWARDS_MAX_LENGTH);

    if (exceededMultilingualAwards.length) {
      const formErrors = exceededMultilingualAwards.map(({ index }) => ({
        name: ["multilingual_data", index, "award"],
        errors: [`Awards cannot exceed ${AWARDS_MAX_LENGTH} characters`],
      }));

      form.setFields(formErrors);
      notify.error({
        message: `Language Entry #${exceededMultilingualAwards[0].index + 1} awards exceeds allowed limit`,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: values.name,
        alternateName: values.alternateName,
        jobTitle: values.jobTitle,
        slug: values.slug,
        image_url: values.image_url,
        gender: values.gender,
        birthDate: values.birthDate
          ? dayjs(values.birthDate).format("YYYY-MM-DD")
          : null,
        deathDate: values.deathDate
          ? dayjs(values.deathDate).format("YYYY-MM-DD")
          : null,
        description: values.description,
        nationality: values.nationality,
        imdb_id: values.imdb_id,
        sameAs: values.sameAs || [],
        award: values.award || [],
        multilingual_data: values.multilingual_data || [],
      };

      let response;
      if (talent_id === "new") {
        response = await masterService.createTalent(payload);
      } else {
        response = await masterService.updateTalent(talent_id, payload);
      }

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Talent updated successfully",
        });
        navigate("/talent");
      } else {
        notify.error({
          message: response.data.message || "Failed to update talent",
        });
      }
    } catch (error) {
      console.error("Error updating talent:", error);
      notify.error({ message: "Error updating talent" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/talent");
  };

  useEffect(() => {
    setPageTitle(talent_id === "new" ? "Add Talent" : "Edit Talent");
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();

    if (talent_id !== "new") {
      fetchTalent();
    }
  }, [setPageTitle, talent_id, fetchTalent, notify]);

  return (
    <div className="min-h-screen">
      <Card>
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          className="space-y-6"
        >
          {/* Basic Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <UserOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Basic Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Name
                    </span>
                  }
                  name="name"
                  rules={[
                    { required: true, message: "Please enter name" },
                    { validator: noWhitespaceValidator },
                    { max: 255, message: "Name cannot exceed 255 characters" },
                  ]}
                >
                  <Input placeholder="Enter name" className="rounded-lg" maxLength={255}
                     onChange={(e) => {
                      const value = sanitizeBilingualTextInput(e.target.value);
                      form.setFieldsValue({ name: value });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Alternate Name
                    </span>
                  }
                  name="alternateName"
                  rules={[
                    { max: 255, message: "Alternate name cannot exceed 255 characters" },
                  ]}
                >
                  <Input
                    placeholder="Enter alternate name"
                    className="rounded-lg"
                    maxLength={255}
                    onChange={(e) => {
                      const value = sanitizeBilingualTextInput(e.target.value);
                      form.setFieldsValue({ alternateName: value });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Job Title
                    </span>
                  }
                  name="jobTitle"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select job title"
                    className="rounded-lg"
                  >
                    <Option value="Actor">Actor</Option>
                    <Option value="Director">Director</Option>
                    <Option value="Producer">Producer</Option>
                    <Option value="Writer">Writer</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Slug
                    </span>
                  }
                  name="slug"
                  rules={[
                    { max: 255, message: "Slug cannot exceed 255 characters" },
                  ]}
                >
                  <Input
                    disabled
                    placeholder="Enter slug"
                    className="rounded-lg"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Gender
                    </span>
                  }
                  name="gender"
                >
                  <Select placeholder="Select gender" className="rounded-lg">
                    <Option value="Male">Male</Option>
                    <Option value="Female">Female</Option>
                    <Option value="Other">Other</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Nationality
                    </span>
                  }
                  name="nationality"
                  rules={[
                    { max: 255, message: "Nationality cannot exceed 255 characters" },
                  ]}
                >
                  <Input
                    placeholder="Enter nationality"
                    className="rounded-lg"
                    maxLength={255}
                    onChange={(e) => {
                      const value = sanitizeBilingualTextInput(e.target.value);
                      form.setFieldsValue({ nationality: value });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      IMDB ID
                    </span>
                  }
                  name="imdb_id"
                  rules={[
                    { max: 255, message: "IMDB ID cannot exceed 255 characters" },
                  ]}
                >
                  <Input placeholder="Enter IMDB ID" className="rounded-lg" maxLength={255} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Birth Date
                    </span>
                  }
                  name="birthDate"
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    placeholder="Select birth date"
                    className="rounded-lg"
                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Death Date
                    </span>
                  }
                  name="deathDate"
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD-MM-YYYY"
                    placeholder="Select death date"
                    className="rounded-lg"
                    disabledDate={(current) => current && current > dayjs().endOf("day")}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Image Configuration Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <PictureOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Image Configuration
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Talent Image
                    </span>
                  }
                  name="image_url"
                >
                  <ImageUpload
                    label=""
                    name="image_url"
                    width={256}
                    height={256}
                    imageType="avatars"
                    value={form.getFieldValue("image_url")}
                    onChange={(val) => form.setFieldsValue({ image_url: val })}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          {/* Description Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <FileTextOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Description
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Description
                    </span>
                  }
                  name="description"
                  rules={[
                    { max: 5000, message: "Description cannot exceed 5000 characters" },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter description"
                    className="rounded-lg"
                    maxLength={5000}
                    showCount
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          {/* Same As Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <UserOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Same As (URLs)
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item name="sameAs">
                  <Select
                    mode="tags"
                    placeholder="Enter URLs and press Enter to add tags"
                    className="rounded-lg"
                    tokenSeparators={[","]}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
          {/* Awards Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <UserOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Awards
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  name="award"
                  rules={[
                    {
                      validator: (_, value) => {
                        const length = getCommaSeparatedLength(value);
                        if (length > AWARDS_MAX_LENGTH) {
                          return Promise.reject(
                            new Error(
                              `Awards cannot exceed ${AWARDS_MAX_LENGTH} characters`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    mode="tags"
                    placeholder="Enter awards and press Enter to add tags"
                    className="rounded-lg"
                    tokenSeparators={[","]}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Multilingual Data Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <GlobalOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Multilingual Data
                </Title>
              </div>
            </div>

            <Form.List name="multilingual_data">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        marginBottom: 16,
                        padding: 16,
                        border: "1px solid #d9d9d9",
                        borderRadius: 8,
                      }}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <Title
                          level={5}
                          className="!mb-0"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language Entry #{name + 1}
                        </Title>
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          Remove
                        </Button>
                      </div>

                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "lang_id"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Language
                              </span>
                            }
                            rules={[
                              {
                                required: true,
                                message: "Please select a language",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select language"
                              showSearch
                              filterOption={(input, option) =>
                                (option?.label ?? "")
                                  .toLowerCase()
                                  .includes(input.toLowerCase())
                              }
                              className="rounded-lg"
                            >
                              {languages.map((lang) => (
                                <Option
                                  key={lang.lang_id}
                                  value={lang.lang_id}
                                  label={lang.lang_name}
                                >
                                  {lang.lang_name}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "name"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Name
                              </span>
                            }
                            rules={[
                              {
                                max: 255,
                                message: "Name cannot exceed 255 characters",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter name in this language"
                              className="rounded-lg"
                              maxLength={255}
                              onChange={(e) => {
                                const value = sanitizeBilingualTextInput(e.target.value);
                                const multilingualData =
                                  form.getFieldValue("multilingual_data") || [];
                                multilingualData[name] = {
                                  ...(multilingualData[name] || {}),
                                  name: value,
                                };
                                form.setFieldsValue({
                                  multilingual_data: multilingualData,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "alternateName"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Alternate Name
                              </span>
                            }
                            rules={[
                              {
                                max: 255,
                                message:
                                  "Alternate name cannot exceed 255 characters",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter alternate name in this language"
                              className="rounded-lg"
                              maxLength={255}
                              onChange={(e) => {
                                const value = sanitizeBilingualTextInput(e.target.value);
                                const multilingualData =
                                  form.getFieldValue("multilingual_data") || [];
                                multilingualData[name] = {
                                  ...(multilingualData[name] || {}),
                                  alternateName: value,
                                };
                                form.setFieldsValue({
                                  multilingual_data: multilingualData,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={12}>
                          <Form.Item
                            {...restField}
                            name={[name, "nationality"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Nationality
                              </span>
                            }
                            rules={[
                              {
                                max: 255,
                                message:
                                  "Nationality cannot exceed 255 characters",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Enter nationality in this language"
                              className="rounded-lg"
                              maxLength={255}
                              onChange={(e) => {
                                const value = sanitizeBilingualTextInput(e.target.value);
                                const multilingualData =
                                  form.getFieldValue("multilingual_data") || [];
                                multilingualData[name] = {
                                  ...(multilingualData[name] || {}),
                                  nationality: value,
                                };
                                form.setFieldsValue({
                                  multilingual_data: multilingualData,
                                });
                              }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            {...restField}
                            name={[name, "description"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Description
                              </span>
                            }
                            rules={[
                              {
                                max: 5000,
                                message:
                                  "Description cannot exceed 5000 characters",
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              placeholder="Enter description in this language"
                              className="rounded-lg"
                              maxLength={5000}
                              showCount
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24}>
                          <Form.Item
                            {...restField}
                            name={[name, "award"]}
                            label={
                              <span
                                className="font-semibold"
                                style={{ color: "rgba(0, 0, 0, 0.88)" }}
                              >
                                Awards
                              </span>
                            }
                            rules={[
                              {
                                validator: (_, value) => {
                                  const length = getCommaSeparatedLength(value);
                                  if (length > AWARDS_MAX_LENGTH) {
                                    return Promise.reject(
                                      new Error(
                                        `Awards cannot exceed ${AWARDS_MAX_LENGTH} characters`
                                      )
                                    );
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Select
                              mode="tags"
                              placeholder="Enter awards and press Enter to add tags"
                              className="rounded-lg"
                              tokenSeparators={[","]}
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className="rounded-lg"
                    >
                      Add Language
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {talent_id === "new" ? "Add Talent" : "Update Talent"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default EditCast;
